<template>
  <!-- TODO: Responsiveness (change position of buttons on mobile) -->
  <dara-single-content-layout container-size="lg">
    <template v-if="track" #content>
      <dara-row class="d-flex align-items-center">
        <dara-col :xs="24" :span="20">
          <dara-header>
            {{ track.name }}
          </dara-header>
        </dara-col>
        <dara-col :xs="24" :span="4" justify="right">
          <div class="d--flex justify-content--space-between align-items--center">
            <dara-button v-if="canEdit">
              <dara-track-link
                :id="track.id"
                action="edit">
                {{ $t('edit') }}
              </dara-track-link>
            </dara-button>
            <dara-button v-if="canEdit" class="color--danger" @click="setDeleteTrack(track)">
              {{ $t('delete') }}
            </dara-button>
          </div>
        </dara-col>
      </dara-row>

      <dara-modal-delete
        v-if="showModal(track)"
        :content="$t('are-you-sure-you-want-to-delete-this-track')"
        @close="setDeleteTrack(null)"
        @confirm="onDeleteTrack(track)">
      </dara-modal-delete>

      <dara-row>
        <dara-col>
          <dara-button
            v-if="!isOwner && !following"
            class="btn btn-success mb-3"
            size="sm"
            @click="follow()">
            {{ $t('follow') }}
          </dara-button>
          <dara-button
            v-if="!isOwner && following"
            class="btn btn-danger mb-3"
            size="sm"
            @click="unfollow()">
            {{ $t('unfollow') }}
          </dara-button>

          <dara-p v-if="track.description" class="dara-u-text--medium">
            {{ track.description }}
          </dara-p>
          <small class="text-muted dara-u-text--small d-block mt-2">
            <strong>{{ $t('created-by') }}:</strong>
            <dara-user-link :id="track.user.id" class="color--primary">
              {{ isOwner ? $t('you-caps') : track.user.name }}
            </dara-user-link>
          </small>
          <small class="text-muted dara-u-text--small d-block">
            <strong>{{ $t('department') }}:</strong> {{ department }}
          </small>
          <small class="text-muted dara-u-text--small d-block mb-2">
            <strong>{{ $t('access') }}:</strong> {{ track.access === 0 ? $t('public') : $t('department-only') }}
          </small>
          <small class="text-muted dara-u-text--small d-block">
            <strong>{{ $t('reading-time') }}:</strong> {{ track.total_time }} min
          </small>
          <small class="text-muted dara-u-text--small d-block mb-2">
            <strong>{{ $t('number-of-articles') }}:</strong> {{ track.number_of_articles }}
          </small>
          <small class="text-muted dara-u-text--small d-block mb-3">
            {{ $t('updated') }} {{ track.updated_at }}
          </small>
        </dara-col>
      </dara-row>
      <hr>
      <dara-invisible v-if="!!articles && articles.length > 0 && track.number_of_articles > 0">
        <div v-for="(article, index) in articles" :key="index">
          <dara-article-card :article="article" :track="track.id" />
          <hr>
        </div>
        <div class="d-flex justify-content-center mt-3">
          <dara-pagination
            :page.sync="pagination.page"
            :total="pagination.total"
            :page-size="pagination.limit"
            @current-change="fetchTrackArticles" />
        </div>
      </dara-invisible>
      <dara-invisible v-if="track.number_of_articles === 0">
        <dara-p class="opacity--50 my--md">( {{ $t('no-articles') }} )</dara-p>
      </dara-invisible>
    </template>
  </dara-single-content-layout>
</template>

<script>
import { toastError } from '@/services/toastService';
import { getTrackById, removeTrack, updateTrackVisits } from '@/modules/track/track.service';
import { fetchTrackArticles } from '@/modules/article/article.service';
import { getTrackFollowStatus, followTrack, unfollowTrack } from '@/modules/track/track.resource';
import { translate } from '@/services/translate.service';
import { goToDeniedTrack, goToTrackNotFound } from '@/modules/track/track.routes';
import { getters } from '@/modules/user/user.store';
import { getDepartmentById } from '@/modules/department/department.service';
import { TRACK_ERRORS } from '@/modules/track/track.constants';
import { USERS_ROLES } from '@/modules/user/user.constants';
import { goToBasePage } from '@/router/route.service';

import DaraUserLink from '@/modules/user/components/UserLink.vue';
import DaraArticleCard from '@/modules/article/components/ArticleCard.vue';
import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';

export default {
  name: 'TrackViewPage',
  components: {
    DaraUserLink,
    DaraArticleCard,
    DaraSingleContentLayout,
  },
  data() {
    return {
      track: null,
      following: false,
      articles: null,
      department: null,
      pagination: {
        page: 1,
        limit: 5,
      },
      user: getters.getUser(),
      trackToDelete: null,
    };
  },
  computed: {
    id() {
      return this.$route.params?.id;
    },
    canEdit() {
      return (this.user.permission > USERS_ROLES.WRITER || (this.track.user.id === this.user.id && this.user.permission > USERS_ROLES.READER));
    },
    isOwner() {
      return this.user.id === this.track.user.id;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      getTrackById(this.id)
        .then(data => {
          this.track = data.data;
          this.getDepartment(this.track.department_id);
          this.fetchTrackArticles();
          getTrackFollowStatus(this.track.id)
            .then(result => {
              this.following = result.data;
            })
            .catch(() => {
              this.following = false;
              toastError(`${translate('error-getting-the-following-status')}`);
            });
          updateTrackVisits({ ...this.track });
        })
        .catch(err => {
          this.track = [];
          if ((err.response.data.errors === TRACK_ERRORS[err.response.status] && err.response.status === 404)) {
            goToTrackNotFound(this.$router);
          } else if (err.response.data.errors === TRACK_ERRORS[err.response.status] && err.response.status === 403) {
            goToDeniedTrack(this.$router);
          } else {
            toastError(`${translate('error-getting-track')}`);
          }
        });
    },
    fetchTrackArticles() {
      fetchTrackArticles(this.track, this.pagination)
        .then(data => {
          this.articles = data.data;
          Object.assign(this.pagination, data.pagination);
        })
        .catch(() => toastError(`${translate('error-getting-articles-from-track')}`));
    },
    follow() {
      followTrack(this.track.id)
        .then(() => {
          this.following = true;
        })
        .catch(() => toastError(`${translate('error-on-following-track')}`));
    },
    unfollow() {
      unfollowTrack(this.track.id)
        .then(() => {
          this.following = false;
        })
        .catch(() => toastError(`${translate('error-on-unfollowing-track')}`));
    },
    getDepartment(department) {
      getDepartmentById(department)
        .then(data => {
          this.department = data.name;
        }).catch(() => {
          toastError(`${translate('error-getting-tracks-department')}`);
        });
    },
    setDeleteTrack(track) {
      this.trackToDelete = track;
    },
    showModal(track) {
      return this.trackToDelete && this.trackToDelete.id === track.id;
    },
    onDeleteTrack(track) {
      removeTrack(track).then(() => goToBasePage());
    },
  },
};
</script>
